import { useState, useContext } from "react"
import {
    ActionBarSelector,
    IconCSVDocument,
    IconDownload,
    IconRefresh,
    IconShoveling,
    IconWarning,
    spacingM,
    spacingS,
    colorFunctionalGray30,
    colorAttentionYellow50,
    colorAttentionRed50,
    colorMiscGray90,
    colorMiscGray40,
    colorFunctionalGray60,
    colorFunctionalBlack,
    colorAttentionBlue50,
} from "@rhumbix/rmbx_design_system_web"
import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"
import { ReduxState } from "../../common/types"
import { fetchRecentTKExportSubmissions } from "../actions"
import { ThunkDispatch } from "redux-thunk"
import { Tooltip as ReactTooltip } from "react-tooltip"
import { format } from "date-fns"
import { getAsDate } from "../../common/ts-utils"
import { DATE_MMM_D_YYYY } from "../../common/constants"
import * as API from "../../api"
import { downloadFile } from "../../websockets/actions"
import { tExportChecklistOption } from "../../toolbar/types"
import { SideRailContext } from "../../components/SideRail/SideRailContext"
import { _call } from "../../api/util"
import { setExports } from "../../actions"

const DownloadSelector = (): JSX.Element => {
    const downloadOptions = useSelector((state: ReduxState) => state.exportCSV.exportSubmissions)
    const nextUrl = useSelector((state: ReduxState) => state.exportCSV.next)
    const fetchErrors = useSelector((state: ReduxState) => state.exportCSV.fetchExportSubmissionErrors)
    const [selectedExport, setSelectedExport] = useState<any>(null)
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
    const { enableSideRail } = useContext(SideRailContext)

    const actionBarOptions = nextUrl
        ? [
              ...downloadOptions,
              {
                  id: "more",
                  export_name: "Load More",
              },
          ]
        : downloadOptions

    const fetchMoreOptions = () => {
        _call("GET", nextUrl, null).then(response => {
            dispatch(setExports([...downloadOptions, ...response.results], response.next))
        })
    }

    return (
        <ActionBarSelector
            id={"download-selector"}
            menuStyles={{ right: 0, marginTop: 6, width: 300 }}
            dropdownSubtitleStyles={{
                backgroundColor: colorFunctionalGray30,
                color: colorFunctionalBlack,
                fontSize: 14,
                fontWeight: 400,
                paddingLeft: 16,
                height: 40,
            }}
            dropdownSubtitle="Downloads"
            keepOpenOnSelect={true}
            noOptionsComponent={() => {
                return fetchErrors ? (
                    <Option>
                        <IconWarning height={16} width={16} color={colorAttentionYellow50} />
                        <OptionLabel>Failed to load downloads</OptionLabel>
                        <IconRefresh
                            onClick={() => dispatch(fetchRecentTKExportSubmissions())}
                            style={{ marginLeft: "auto", marginRight: 6 }}
                            width={16}
                            height={16}
                            color={colorFunctionalGray60}
                        />
                    </Option>
                ) : (
                    <Option>
                        <IconShoveling height={16} width={16} color={colorMiscGray40} />
                        <OptionLabel>No Recent Exports</OptionLabel>
                    </Option>
                )
            }}
            onlyDisplayIcon={true}
            options={actionBarOptions.map(op => {
                return {
                    value: op.id,
                    label: op.export_name,
                    data: op,
                }
            })}
            onChange={(e: any) => {
                setSelectedExport(e.value)
            }}
            placeholder={""}
            renderIcon={(color: string) => <IconDownload color={color} height={spacingM} />}
            renderOption={(option: Record<string, any>) => {
                const { value, label, data } = option
                if (value === "more") {
                    return (
                        <SmallOption>
                            <LoadMore onClick={fetchMoreOptions}>Load More</LoadMore>
                        </SmallOption>
                    )
                }
                return (
                    <Option>
                        <IconCSVDocument width={spacingM} height={spacingM} color={colorMiscGray40} />
                        <OptionLabelContainer>
                            <OptionLabel>{label}</OptionLabel>
                            <OptionSubtitle>
                                {data.filters.start_date || data.filters.startDate
                                    ? `${format(
                                          getAsDate(data.filters.start_date ?? data.filters.startDate),
                                          DATE_MMM_D_YYYY
                                      )} -
                              ${format(getAsDate(data.filters.end_date ?? data.filters.endDate), DATE_MMM_D_YYYY)}`
                                    : ""}
                            </OptionSubtitle>
                        </OptionLabelContainer>
                        <div style={{ marginLeft: "auto", marginRight: 6 }}>
                            {data.processing_status === "COMPLETED" ? (
                                <>
                                    <IconRefresh
                                        height={16}
                                        width={16}
                                        style={{ marginRight: 8 }}
                                        onClick={() => {
                                            const initialFilters = {
                                                ...data.filters,
                                                startDate: getAsDate(data.filters.startDate),
                                                endDate: getAsDate(data.filters.endDate),
                                            }
                                            enableSideRail({
                                                flow: "RUN_EXPORT",
                                                selectedExports: data.export_formats as tExportChecklistOption[],
                                                initialFilters,
                                                cameFrom: "weeklyView",
                                                includeSignatureImages: !!data?.include_signature_images,
                                            })
                                        }}
                                    />
                                    <IconDownload
                                        width={16}
                                        height={16}
                                        color={colorFunctionalGray60}
                                        onClick={async () => {
                                            if (data.number_of_runs === 1) {
                                                const a = document.createElement("a")
                                                a.href = data.output_files[0]
                                                a.target = "_blank"
                                                document.body.appendChild(a)
                                                a.click()
                                            } else {
                                                const res = await API.getTKExportSubmissionResults(data.id)
                                                downloadFile(res, "exports.zip")
                                            }
                                        }}
                                    />
                                </>
                            ) : data.processing_status === "RUNNING" ? (
                                // For running, we can show a spinner and tooltip for where we're at in the process
                                <>
                                    <ReactTooltip
                                        id={`${data.id}-tooltip`}
                                        place="bottom"
                                        variant="dark"
                                        float={true}
                                        style={{
                                            zIndex: 1100,
                                            maxWidth: 300,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "keep-all",
                                        }}
                                        content={data.processing_result}
                                    />
                                    <img
                                        style={{ width: 16, height: 16 }}
                                        // eslint-disable-next-line @typescript-eslint/no-require-imports
                                        src={require("gradient-spinner.gif")}
                                        alt={"loading"}
                                        id={data.id}
                                        data-tooltip-id={`${data.id}-tooltip`}
                                    />
                                </>
                            ) : (
                                // Only value left is FAILED, so show an error icon
                                <>
                                    <ReactTooltip
                                        id={`${data.id}-tooltip`}
                                        place="bottom"
                                        variant="dark"
                                        float={true}
                                        style={{
                                            zIndex: 1100,
                                            maxWidth: 300,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "keep-all",
                                        }}
                                        content={data.processing_result}
                                    />
                                    <IconWarning
                                        width={16}
                                        height={16}
                                        color={colorAttentionRed50}
                                        data-tooltip-id={`${data.id}-tooltip`}
                                    />
                                </>
                            )}
                        </div>
                    </Option>
                )
            }}
            value={selectedExport}
        />
    )
}

export default DownloadSelector

const LoadMore = styled.span`
    color: ${colorAttentionBlue50};
    text-decoration: underline;
    font-size: 12px;
`

const Option = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-left: ${spacingS};
    height: 48px;
    margin-left: 10px;
    overflow-x: hidden;
    border-bottom: 1px solid ${colorFunctionalGray30};
`

const SmallOption = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 24px;
    overflow-x: hidden;
    justify-content: center;
`

const OptionLabelContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${spacingM};
`
const OptionLabel = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: ${colorMiscGray90};
`

const OptionSubtitle = styled.div`
    font-size: 12px;
    font-weight: 400;
    margin-top: 4px;
    color: ${colorMiscGray40};
`
